import { useEffect, useState } from 'react';

import { Button, Dropdown, Input, Modal, TagInput } from '@components';
import { LEAD_NAME_MAX_LENGTH } from '@constants/generic';
import {
  INPUT_BASE_CLASSNAME,
  INPUT_FOCUS_CLASSNAME
} from '@features/lead-management/constants/common';
import { priorityOptions } from '@features/lead-management/constants/lead-info';
import useNotify from '@hooks/notify';
import { getLeadNameValidationError } from '@utils/validations';
import { Tag } from 'types/lead-management';

import {
  useCreateTagMutation,
  useGetTagsQuery,
  useUpdateTagMutation
} from '../queries/LeadManagementQuery';
import { LeadEditModalProps } from '../types/leadDetails';

const LeadEditModal = ({
  isOpen,
  onClose,
  leadData,
  statusOptions,
  assigneeOptions,
  onSave,
  isSaving = false,
  onTagRenameSuccess,
  isAssigneeDisabled = false
}: LeadEditModalProps) => {
  const organizationId = localStorage.getItem('organizationId') || '';
  const { createSuccessAlert, createErrorAlert } = useNotify();

  const { data: tagData } = useGetTagsQuery(
    { organizationId },
    { refetchOnMountOrArgChange: true }
  );
  const [createTags] = useCreateTagMutation();
  const [updateTag] = useUpdateTagMutation();

  const [formData, setFormData] = useState({
    name: leadData.name || '',
    status: leadData.status?.id || '',
    assignee: leadData.assignee?.id || 'unassigned',
    priority: leadData.priority || 'low',
    tags: leadData.tags || []
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedTags, setSelectedTags] = useState<Tag[]>(leadData.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [apiTagOptions, setApiTagOptions] = useState<Tag[]>(tagData || []);

  useEffect(() => {
    if (tagData) {
      setApiTagOptions(tagData);
    }
  }, [tagData]);

  useEffect(() => {
    if (isOpen && leadData) {
      setFormData({
        name: leadData.name || '',
        status: leadData.status?.id || '',
        assignee: leadData.assignee?.id || 'unassigned',
        priority: leadData.priority || 'low',
        tags: leadData.tags || []
      });
      setSelectedTags(leadData.tags || []);
    }
  }, [isOpen, leadData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    const nameError = getLeadNameValidationError(
      formData.name,
      LEAD_NAME_MAX_LENGTH
    );
    if (nameError) {
      newErrors.name = nameError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateName = (name: string) => {
    const nameError = getLeadNameValidationError(name, LEAD_NAME_MAX_LENGTH);
    setErrors((prev) => ({ ...prev, name: nameError || '' }));
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    if (field === 'name') {
      validateName(String(value));
      return;
    }

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const hasErrors = Object.values(errors).some((e) => !!e);

  const handleTagsChange = async (selectedTagsList: Tag[]) => {
    const newCustomTags = selectedTagsList.filter((tag: Tag) =>
      tag.id.startsWith('custom-')
    );
    const existingTags = selectedTagsList.filter(
      (tag: Tag) => !tag.id.startsWith('custom-')
    );
    let createdTag: Tag | null = null;

    if (newCustomTags.length > 0) {
      const tagToCreate = newCustomTags[0];

      try {
        const response = await createTags({
          organizationId,
          name: tagToCreate.name.toLowerCase(),
          color: tagToCreate.color || '#F2E1E9'
        });

        if ('data' in response) {
          createdTag = {
            id: response.data.id,
            name: response.data.name,
            color: response.data.color
          };

          setApiTagOptions([createdTag, ...apiTagOptions]);

          createSuccessAlert(`Tag "${createdTag.name}" created successfully`);
        } else {
          createErrorAlert(`Failed to create tag "${tagToCreate.name}"`);
        }
      } catch (error) {
        createErrorAlert(
          `Failed to create tag "${tagToCreate.name}". Please try again.`
        );
      }
    }

    let finalSelectedTags = existingTags;
    if (createdTag?.id) {
      finalSelectedTags = [...existingTags, createdTag];
    }

    setSelectedTags(finalSelectedTags);
    setFormData((prev) => ({
      ...prev,
      tags: finalSelectedTags
    }));
    setTagInput('');
  };

  const handleUpdateTag = async (tagItem: Tag) => {
    if (!organizationId || !tagItem.id) return;

    try {
      const response = await updateTag({
        organizationId,
        id: tagItem.id,
        name: tagItem.name,
        color: tagItem.color || '#F2E1E9'
      });

      if ('data' in response && response.data) {
        setApiTagOptions(
          apiTagOptions.map((tag) =>
            tag.id === tagItem.id ? response.data : tag
          )
        );
        createSuccessAlert(`Tag "${response.data.name}" updated successfully`);
        const isSelectedTag = selectedTags.find((tag) => tag.id === tagItem.id);

        if (isSelectedTag) {
          const updatedSelectedTags = selectedTags.map((tag) =>
            tag.id === tagItem.id ? response.data : tag
          );
          setSelectedTags(updatedSelectedTags);
          setFormData((prev) => ({
            ...prev,
            tags: updatedSelectedTags
          }));
        }
        onTagRenameSuccess();
      } else {
        createErrorAlert(`Failed to update tag "${tagItem.name}"`);
      }
    } catch (error) {
      createErrorAlert(
        `Failed to update tag "${tagItem.name}". Please try again.`
      );
    }
  };

  const handleSave = () => {
    if (validateForm()) {
      const foundStatus = statusOptions.find((s) => s.id === formData.status);
      const foundAssignee = assigneeOptions.find(
        (a) => a.id === formData.assignee
      );

      const updatedData = {
        name: formData.name,
        status: foundStatus
          ? {
              id: String(foundStatus.id),
              value: foundStatus.value
            }
          : {
              id: leadData?.status?.id,
              value: leadData?.status?.value
            },
        assignee: foundAssignee
          ? {
              id: String(foundAssignee.id),
              name: foundAssignee.value
            }
          : {
              id: leadData?.assignee?.id,
              name: leadData?.assignee?.name
            },
        priority: formData.priority,
        tags: selectedTags
      };
      onSave(updatedData);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onCancel={onClose}
      className="max-h-[90vh] w-full max-w-2xl rounded-lg bg-white shadow-xl"
      isCloseIconRequired={!isSaving}
    >
      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900">
            Edit Information
          </h2>
        </div>

        <div className="flex flex-wrap gap-6">
          <div className="w-[48%]">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Full Name *
            </label>
            <Input
              value={formData.name}
              handleInputChange={(value) => handleInputChange('name', value)}
              placeholder="Enter full name"
              className="w-full"
              error={!!errors.name}
              errorMessage={errors.name}
              disabled={leadData?.is_onboarded}
              inputStyle={`${INPUT_BASE_CLASSNAME} ${
                leadData?.is_onboarded ? '!text-cofeeNeutral7' : ''
              }`}
            />
          </div>

          <div className="w-[48%]">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Lead Status
            </label>
            <Dropdown
              currentFilter={formData.status}
              filterOptions={statusOptions}
              onSelectFilter={(value) => handleInputChange('status', value)}
              className={`h-12.5 w-full ${INPUT_FOCUS_CLASSNAME}`}
              buttonClassName="w-full h-12.5 !bg-white !border-gray-300 !text-gray-900"
              optionsStyle="w-full"
              optionItemClassName="break-all"
              labelStyle="break-all"
              usePortal
            />
            {errors.status && (
              <div className="mt-1 text-xs text-red-500">{errors.status}</div>
            )}
          </div>

          <div className="w-[48%]">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Assignee
            </label>
            <Dropdown
              currentFilter={formData.assignee}
              filterOptions={assigneeOptions}
              onSelectFilter={(value) => handleInputChange('assignee', value)}
              className={`h-11 w-full ${INPUT_FOCUS_CLASSNAME}`}
              buttonClassName="w-full h-11 !bg-white !border-gray-300 !text-gray-900"
              optionsStyle="w-full"
              usePortal
              disabled={isAssigneeDisabled}
            />
            {errors.assignee && (
              <div className="mt-1 text-xs text-red-500">{errors.assignee}</div>
            )}
          </div>

          <div className="w-[48%]">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Priority
            </label>
            <Dropdown
              currentFilter={formData.priority}
              filterOptions={priorityOptions}
              onSelectFilter={(value) => handleInputChange('priority', value)}
              className={`h-11 w-full ${INPUT_FOCUS_CLASSNAME}`}
              buttonClassName="w-full h-11 !bg-white !border-gray-300 !text-gray-900"
              optionsStyle="w-full"
              usePortal
            />
            {errors.priority && (
              <div className="mt-1 text-xs text-red-500">{errors.priority}</div>
            )}
          </div>

          <div className="w-full">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Tags
            </label>
            <div className="w-full">
              <TagInput
                value={tagInput}
                onChange={(val) => {
                  setTagInput(val.toLowerCase());
                }}
                onTagsChange={handleTagsChange}
                selectedTags={selectedTags}
                tagOptions={apiTagOptions}
                placeholder="Select or create tags"
                className="w-full"
                onUpdateTag={handleUpdateTag}
                selectorProps={{ positionFixed: true }}
              />
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3 border-t pt-6">
          <Button
            variant="tertiary"
            label="Cancel"
            handleClick={onClose}
            className="h-11 px-6"
          />
          <Button
            variant="primary"
            label="Save"
            loading={isSaving}
            handleClick={handleSave}
            className="h-11 px-6"
            disabled={isSaving || hasErrors}
          />
        </div>
      </div>
    </Modal>
  );
};

export default LeadEditModal;
